/**
 * Read adapters for stored `heat-grid` payloads (plots plan Q; contract in
 * `../contract.ts`, stored shapes in docs/deck-design-system/plots/read-adapters.md).
 *
 * Shape only: fields are renamed and copied, never computed, rounded, sorted
 * or crowned. Two stored shapes keep a legacy step, marked `isLegacy`:
 *
 * - a matrix-rating cell stored before `normalizedValue` was emitted (the
 *   field absent, not null) derives its heat from the value on the column's
 *   own bounds, flipped for a lower-is-better measure, as today's slide did;
 * - a cut split chart stored before Python stamped its `plot` maps each share
 *   to heat as share / 100, as `segmentedHeatmapView` did;
 * - a matrix-rating view stored before Python stamped its `plot` carries no
 *   polarity stamps (`keyReversed`, a change cell's `direction`), so they are
 *   read off its measures' `polarity` as `stamp_polarity` states them.
 *
 * A matrix-rating slide or a split chart that carries its `plot` draws it as
 * stamped (`readStampedHeatGrid`). Chrome strings the stored payload carries
 * (`displayLabels`) are mapped onto label keys; nothing is filled in English.
 */

import {
  HeatGridCell,
  HeatGridColumn,
  HeatGridRow,
} from '@digital-twins/slide-schemas/generated/plot-types'
import {
  SegmentRow,
  SegmentSplit,
} from '@digital-twins/slide-schemas/generated/slide-types'

import { PlotReadAdapter, StoredPlotPayload } from '../contract'
import { MatrixRatingSlideData } from '../../slides/renderer-internal-types'

type StoredHeatGrid = StoredPlotPayload<'heat-grid'>

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function finite(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/** A stamped heat-grid plot as the kind's stored payload (Python validated it). */
export function readStampedHeatGrid(plot: unknown): StoredHeatGrid | null {
  if (!isRecord(plot) || plot.kind !== 'heat-grid' || !isRecord(plot.data)) {
    return null
  }
  const data = plot.data
  if (!Array.isArray(data.rows) || !Array.isArray(data.columns) || data.columns.length === 0) {
    return null
  }
  return plot as unknown as StoredHeatGrid
}

// -- deck slide: matrix-rating -------------------------------------------------------

type StoredMatrixRating = Pick<
  MatrixRatingSlideData,
  'type' | 'measures' | 'rows' | 'rowGroups' | 'scale' | 'page' | 'displayLabels'
> & { plot?: unknown }

function legacyHeat(
  cell: { value?: number | null; normalizedValue?: number | null },
  measure: StoredMatrixRating['measures'][number] | undefined,
  scale: StoredMatrixRating['scale'],
): number | null {
  if (cell.normalizedValue === null) {
    return null
  }
  const stamped = finite(cell.normalizedValue)
  if (stamped !== null) {
    return Math.max(0, Math.min(1, stamped))
  }
  // Legacy: a cell stored before `normalizedValue` existed (the field absent).
  const value = finite(cell.value)
  const min = finite(measure?.min) ?? scale?.min ?? null
  const max = finite(measure?.max) ?? scale?.max ?? null
  if (value === null || min === null || max === null) {
    return null
  }
  if (max === min) {
    return 0
  }
  const position = Math.max(0, Math.min(1, (value - min) / (max - min)))
  const polarity = measure?.polarity ?? 'higher_is_better'
  if (polarity === 'middle_is_best') {
    return 1 - Math.abs(2 * position - 1)
  }
  return polarity === 'lower_is_better' ? 1 - position : position
}

/**
 * Legacy: a stored change cell's direction, its sign read in its measure's
 * polarity (`stamp_polarity`'s rule), for a view stored before the stamp.
 */
function legacyDirection(
  value: number | null | undefined,
  measure: StoredMatrixRating['measures'][number] | undefined,
): Pick<HeatGridCell, 'direction'> {
  const figure = finite(value)
  const polarity = measure?.polarity ?? 'higher_is_better'
  if (figure === null || figure === 0 || polarity === 'middle_is_best') {
    return {}
  }
  return { direction: figure > 0 === (polarity === 'higher_is_better') ? 'better' : 'worse' }
}

export const matrixRatingSlideAdapter: PlotReadAdapter<'heat-grid', StoredMatrixRating> = {
  kind: 'heat-grid',
  source: { surface: 'deck-slide', shape: 'matrix-rating' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredMatrixRating {
    return (
      isRecord(stored) &&
      stored.type === 'matrix-rating' &&
      Array.isArray(stored.measures) &&
      Array.isArray(stored.rows)
    )
  },
  read(stored: StoredMatrixRating): StoredHeatGrid {
    const stamped = readStampedHeatGrid(stored.plot)
    if (stamped) {
      return stamped
    }
    const measures = stored.measures || []
    const byId = new Map(measures.map((measure) => [measure.id, measure]))
    const columns: Array<HeatGridColumn> = measures.map((measure) => {
      const isChange = measure.valueFormat === 'signed'
      const column: HeatGridColumn = {
        id: measure.id,
        label: measure.label,
        kind: isChange ? 'change' : 'measure',
        // Legacy formatting: a stored column prints plain at its decimals (default 1).
        valueFormat: isChange ? 'points' : 'mean',
        decimals: measure.decimals ?? 1,
        polarity: measure.polarity ?? 'higher_is_better',
      }
      if (!isChange) {
        column.min = finite(measure.min)
        column.max = finite(measure.max)
        column.minLabel = measure.minLabel ?? null
        column.maxLabel = measure.maxLabel ?? null
      }
      return column
    })
    const changeIds = new Set(columns.filter((column) => column.kind === 'change').map((column) => column.id))
    const heatColumns = columns.filter((column) => column.kind !== 'change')
    const rows: Array<HeatGridRow> = (stored.rows || []).map((row) => ({
      id: row.id,
      label: row.label,
      kind: 'item',
      ...(row.groupId ? { groupId: row.groupId } : {}),
      ...(row.imageUrl ? { imageUrl: row.imageUrl } : {}),
      average: finite(row.average),
      cells: row.cells.map((cell): HeatGridCell => ({
        columnId: cell.measureId,
        value: finite(cell.value),
        heat: changeIds.has(cell.measureId) ? null : legacyHeat(cell, byId.get(cell.measureId), stored.scale),
        mark: cell.isStrongest ? 'strongest' : null,
        ...(changeIds.has(cell.measureId) ? legacyDirection(cell.value, byId.get(cell.measureId)) : {}),
      })),
    }))
    const labels = stored.displayLabels || {}
    const data: StoredHeatGrid['data'] = {
      status: 'ok',
      rows,
      columns,
      labels: {
        ...(labels.avgColumn ? { avg_column: labels.avgColumn } : {}),
        ...(labels.unsupportedScale ? { unsupported_rating_scale: labels.unsupportedScale } : {}),
        ...(labels.scaleLow ? { scale_low: labels.scaleLow } : {}),
        ...(labels.scaleHigh ? { scale_high: labels.scaleHigh } : {}),
      },
    }
    if (stored.rowGroups && stored.rowGroups.length > 0) {
      data.rowGroups = stored.rowGroups.map((group) => ({
        id: group.id,
        label: group.label,
        ...(group.imageUrl ? { imageUrl: group.imageUrl } : {}),
      }))
    }
    if (stored.scale) {
      data.scale = { min: stored.scale.min, max: stored.scale.max }
    }
    // Legacy: no stamped plot, so no stamped key orientation (`stamp_polarity`'s rule).
    if (heatColumns.length > 0 && heatColumns.every((column) => column.polarity === 'lower_is_better')) {
      data.keyReversed = true
    }
    if (stored.page) {
      data.page = { current: stored.page.current, total: stored.page.total }
    }
    return { kind: 'heat-grid', data }
  },
}

// -- deck slide: the cut split chart ---------------------------------------------------

export interface StoredCutSplit {
  segmentRows: ReadonlyArray<SegmentRow>
  segmentSplit: SegmentSplit & { plot?: unknown }
}

/** A split chart stored with its stamped heat-grid. */
export const cutSplitAdapter: PlotReadAdapter<'heat-grid', StoredCutSplit> = {
  kind: 'heat-grid',
  source: { surface: 'deck-slide', shape: 'single-select-bars' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredCutSplit {
    return (
      isRecord(stored) &&
      Array.isArray(stored.segmentRows) &&
      isRecord(stored.segmentSplit) &&
      readStampedHeatGrid(stored.segmentSplit.plot) !== null
    )
  },
  read(stored: StoredCutSplit): StoredHeatGrid {
    return readStampedHeatGrid(stored.segmentSplit.plot) as StoredHeatGrid
  },
}

/**
 * A split chart stored before Python stamped its plot. Legacy: heat is the
 * share over 100, as `segmentedHeatmapView` drew it; kept until the last
 * stored split without a plot is gone.
 */
export const cutSplitLegacyAdapter: PlotReadAdapter<'heat-grid', StoredCutSplit> = {
  kind: 'heat-grid',
  source: { surface: 'deck-slide', shape: 'single-select-bars' },
  isLegacy: true,
  accepts(stored: unknown): stored is StoredCutSplit {
    return (
      isRecord(stored) &&
      Array.isArray(stored.segmentRows) &&
      isRecord(stored.segmentSplit) &&
      Array.isArray(stored.segmentSplit.columns)
    )
  },
  read(stored: StoredCutSplit): StoredHeatGrid {
    const split = stored.segmentSplit
    const columns: Array<HeatGridColumn> = split.columns.map((column) => ({
      id: column.id,
      label: column.label,
      kind: 'option',
      valueFormat: 'percent',
      decimals: split.displayDecimals,
      min: 0,
      max: 100,
      polarity: 'higher_is_better',
    }))
    const rows: Array<HeatGridRow> = stored.segmentRows.map((row) => ({
      id: row.groupId,
      label: row.label,
      kind: 'segment',
      base: row.n,
      thin: row.thin,
      readStrength: row.readStrength ?? null,
      cells: columns.map((column, index) => {
        const value = finite(row.values[index])
        return {
          columnId: column.id,
          value,
          heat: value === null ? null : Math.max(0, Math.min(1, value / 100)),
        }
      }),
    }))
    return {
      kind: 'heat-grid',
      data: {
        status: 'ok',
        rows,
        columns,
        scale: { min: 0, max: 100 },
        emphasizedColumnId: split.readColumnId,
        notes: [...split.notes],
        labels: {},
      },
    }
  },
}

/** The split chart's heat-grid: the stamped plot, else the legacy reader. */
export function readCutSplit(stored: StoredCutSplit): StoredHeatGrid {
  if (cutSplitAdapter.accepts(stored)) {
    return cutSplitAdapter.read(stored)
  }
  return cutSplitLegacyAdapter.read(stored)
}

// -- summary card: the comparison grid (`GridPayload`) ------------------------------

/** The summary `comparison` card as stored (`GridPayload`), plot optional. */
export interface StoredComparisonCard {
  shape: 'comparison'
  table?: {
    columns: Array<{ id: string; label: string; baseSize?: number | null }>
    rows: Array<{ metricId: string; label: string; valuesByColumn: Record<string, number> }>
    unitLabel?: string | null
    decimals?: number | null
  } | null
  scorecard?: {
    rows: Array<{
      metricId: string
      label: string
      score?: number | null
      scoresByAssetId?: Record<string, number> | null
    }>
    assetOptions?: Array<{ id: string; label: string }> | null
  } | null
  plot?: unknown
}

/** What a comparison card read returns: the grid, and the diagnostics rows it leaves off. */
export interface ComparisonCardRead {
  payload: StoredHeatGrid
  isStamped: boolean
  /** Diagnostics measures scored once for the whole sample, left off a per-version grid (disclosed). */
  droppedMeasures: Array<string>
  /** Legacy reads only: today's figure string (whole numbers whole, else one decimal). */
  formatValue?: (value: number) => string
  /** Legacy reads only: what today's grid prints in a cell with no value. */
  emptyCellText?: string
}

const POOLED_ROW_ID = '__overall__'
/** Today's grid: items run down only above this many items (and more items than measures). */
const LEGACY_TRANSPOSE_ABOVE = 4
const LEGACY_EMPTY_CELL = '-'

/** Legacy: today's comparison grid figure rule (`MeasuresByItemsGrid`). */
export function legacyComparisonValue(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}

function uniqueIds(ids: Array<string>): Array<string> {
  const seen = new Map<string, number>()
  return ids.map((id) => {
    const count = seen.get(id) ?? 0
    seen.set(id, count + 1)
    return count === 0 ? id : `${id}:${count}`
  })
}

interface LegacyAxis {
  id: string
  label: string
  base?: number | null
}

/**
 * A comparison card stored before Python stamped its plot. Legacy: today's
 * grid, shape only. Measures run down and items across unless there are more
 * than four items and more items than measures (today's transpose rule);
 * figures as stored, no heat, no mark. A diagnostics row measured once for the
 * whole sample is left off once the study has versions (today's card rule),
 * and the item letters ride on the version names as today.
 */
export function legacyComparisonGrid(
  stored: StoredComparisonCard,
  itemTags: Record<string, string> = {},
): { payload: StoredHeatGrid; droppedMeasures: Array<string> } {
  let measures: Array<LegacyAxis> = []
  let items: Array<LegacyAxis> = []
  let valueAt: (itemId: string, measureIndex: number) => number | null = () => null
  let decimals = 1
  let note: string | undefined
  const droppedMeasures: Array<string> = []
  if (stored.table) {
    const table = stored.table
    const ids = uniqueIds(table.rows.map((row) => row.metricId))
    measures = table.rows.map((row, index) => ({ id: ids[index], label: row.label }))
    items = table.columns.map((subject) => ({ id: subject.id, label: subject.label, base: finite(subject.baseSize) }))
    valueAt = (itemId, index) => finite(table.rows[index].valuesByColumn?.[itemId])
    decimals = finite(table.decimals) ?? 1
    note = table.unitLabel?.trim() || undefined
  } else {
    const scorecard = stored.scorecard
    const options = scorecard?.assetOptions ?? []
    const hasVersions = options.length > 0
    const kept: NonNullable<StoredComparisonCard['scorecard']>['rows'] = []
    for (const row of scorecard?.rows ?? []) {
      const hasPerVersion = Object.keys(row.scoresByAssetId ?? {}).length > 0
      if (hasVersions && !hasPerVersion) {
        droppedMeasures.push(row.label)
        continue
      }
      kept.push(row)
    }
    const ids = uniqueIds(kept.map((row) => row.metricId))
    measures = kept.map((row, index) => ({ id: ids[index], label: row.label }))
    items = hasVersions
      ? options.map((option) => ({
          id: option.id,
          label: itemTags[option.id] ? `${itemTags[option.id]}  ${option.label}` : option.label,
        }))
      : [{ id: POOLED_ROW_ID, label: '' }]
    valueAt = (itemId, index) =>
      hasVersions ? finite(kept[index].scoresByAssetId?.[itemId]) : finite(kept[index].score)
  }
  // No stamped scale or polarity: plain columns, no heat, no mark (today's plain table).
  const format = { valueFormat: 'mean' as const, decimals }
  const itemsDown = items.length > LEGACY_TRANSPOSE_ABOVE && items.length > measures.length
  let rows: Array<HeatGridRow>
  let columns: Array<HeatGridColumn>
  if (itemsDown) {
    columns = measures.map((measure) => ({ id: measure.id, label: measure.label, kind: 'measure', ...format }))
    rows = items.map((item) => ({
      id: item.id,
      label: item.label,
      kind: 'item',
      ...(item.base !== undefined ? { base: item.base } : {}),
      cells: measures.map((measure, index) => ({ columnId: measure.id, value: valueAt(item.id, index), heat: null })),
    }))
  } else {
    columns = items.map((item) => ({ id: item.id, label: item.label, kind: 'option', ...format }))
    rows = measures.map((measure, index) => ({
      id: measure.id,
      label: measure.label,
      kind: 'item',
      cells: items.map((item) => ({ columnId: item.id, value: valueAt(item.id, index), heat: null })),
    }))
  }
  return {
    payload: {
      kind: 'heat-grid',
      data: { status: 'ok', rows, columns, labels: {}, ...(note ? { notes: [note] } : {}) },
    },
    droppedMeasures,
  }
}

/** The comparison card through its stamped plot, else the legacy reader. */
export function readComparisonCard(
  stored: StoredComparisonCard,
  itemTags: Record<string, string> = {},
): ComparisonCardRead {
  // The disclosure under the grid is the card's rule on its stored rows, either way.
  const legacy = legacyComparisonGrid(stored, itemTags)
  const stamped = readStampedHeatGrid(stored.plot)
  if (stamped) {
    return { payload: stamped, isStamped: true, droppedMeasures: legacy.droppedMeasures }
  }
  return {
    payload: legacy.payload,
    isStamped: false,
    droppedMeasures: legacy.droppedMeasures,
    formatValue: legacyComparisonValue,
    emptyCellText: LEGACY_EMPTY_CELL,
  }
}

export const comparisonCardLegacyAdapter: PlotReadAdapter<'heat-grid', StoredComparisonCard> = {
  kind: 'heat-grid',
  source: { surface: 'summary-card', shape: 'comparison' },
  isLegacy: true,
  accepts(stored: unknown): stored is StoredComparisonCard {
    return isRecord(stored) && stored.shape === 'comparison' && (isRecord(stored.table) || isRecord(stored.scorecard))
  },
  read(stored: StoredComparisonCard): StoredHeatGrid {
    return legacyComparisonGrid(stored).payload
  },
}

// -- quick study: a rating grid ------------------------------------------------------

/** A quick-study result as stored, `heat_grid_plot` optional (W6-QS). */
export interface StoredQuickStudyGrid {
  heat_grid_plot?: unknown
}

/**
 * A quick-study rating grid as Python stamped it (`quick_study.grid_plot`), its
 * radar alternative included: read as is. A result stored before the stamp has
 * no field and keeps its browser reader (`buildRadar`, `scorecardRadarData`,
 * `MatrixChart`), named legacy in read-adapters.md.
 */
export const quickStudyHeatGridAdapter: PlotReadAdapter<'heat-grid', StoredQuickStudyGrid> = {
  kind: 'heat-grid',
  source: { surface: 'quick-study', shape: 'heatmap' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredQuickStudyGrid {
    return isRecord(stored) && readStampedHeatGrid(stored.heat_grid_plot) !== null
  },
  read(stored: StoredQuickStudyGrid): StoredHeatGrid {
    return readStampedHeatGrid(stored.heat_grid_plot) as StoredHeatGrid
  },
}

// -- quick study: the monadic scorecard ---------------------------------------------

/** A quick-study result as stored, `scorecard_plot` optional (SC). */
export interface StoredQuickStudyScorecard {
  scorecard_plot?: unknown
}

/**
 * A quick-study monadic scorecard as Python stamped it (SC,
 * `quick_study.grid_plot.quick_study_scorecard_plot`): dimensions down,
 * versions across, base marks, the gap column and the radar alternative, read
 * as is. A result stored before the stamp has no field and keeps its legacy
 * reader (`ScorecardHeatmap`; the stored radar view reads
 * `quickStudyScorecardRadarAdapter`).
 */
export const quickStudyScorecardAdapter: PlotReadAdapter<'heat-grid', StoredQuickStudyScorecard> = {
  kind: 'heat-grid',
  source: { surface: 'quick-study', shape: 'monadic_scorecard' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredQuickStudyScorecard {
    return isRecord(stored) && readStampedHeatGrid(stored.scorecard_plot) !== null
  },
  read(stored: StoredQuickStudyScorecard): StoredHeatGrid {
    return readStampedHeatGrid(stored.scorecard_plot) as StoredHeatGrid
  },
}

export const HEAT_GRID_READ_ADAPTERS = [
  matrixRatingSlideAdapter,
  cutSplitAdapter,
  cutSplitLegacyAdapter,
  comparisonCardLegacyAdapter,
  quickStudyHeatGridAdapter,
  quickStudyScorecardAdapter,
] as const
